import * as z from 'zod';
import {
  FileCategoryEnum,
  FileFormatEnum,
  LessonAgeGroupsEnum,
  SkillLevelValuesEnum,
  SportsEnum,
} from 'types/generated/client';

export const profilePhoto = z.object({
  profileImageFileName: z.string(),
  profileImagePath: z.string(),
  profileImageProvider: z.string(),
  profileImageProviderId: z.string(),
  profileImageProviderUrl: z.string().min(1, 'Photo is required'),
  profileImageUrl: z.string(),
});

export const userFilesSchema = z.object({
  fileCategory: z.nativeEnum(FileCategoryEnum),
  fileName: z.string(),
  fileType: z.string(),
  host: z.string(),
  originalFileName: z.string(),
  path: z.string(),
  provider: z.string(),
  providerUrl: z.string(),
  url: z.string(),
  fileFormat: z.enum([FileFormatEnum.Image, FileFormatEnum.Video, FileFormatEnum.Audio]),
  providerId: z.string(),
  userId: z.string(),
});

export const photosSchema = z.object({
  photo: profilePhoto,
  userFiles: z.array(userFilesSchema).optional(),
});

export const aboutMeSchema = z.object({
  aboutMe: z.string(),
});

export const prepareSchema = z.object({
  coachLessonHowToPrepare: z.string(),
});

export const getExperienceSchema = (ratingSchema: Record<string, z.ZodTypeAny>) =>
  z.object({
    hasPprId: z.boolean().optional(),
    isIptpaMember: z.boolean().optional(),
    isQuatroPickleballMember: z.boolean().optional(),
    hasPciId: z.boolean().optional(),
    rating: z.object(ratingSchema),
    coachingStartYear: z
      .union([
        z
          .number()
          .int()
          .min(0, { message: 'Value must be at least 0' })
          .max(50, { message: 'Value must be at most 50' }),
        z.nan(),
      ])
      .optional(),
  });

const dynamicAgeGroupSchemaObject = Object.values(LessonAgeGroupsEnum).reduce((schema, group) => {
  schema[group] = z.boolean().optional();
  return schema;
}, {} as Record<string, z.ZodTypeAny>);

const skillLevelSchemaObject = Object.values(SkillLevelValuesEnum).reduce((schema, level) => {
  schema[level] = z.boolean().optional();
  return schema;
}, {} as Record<string, z.ZodTypeAny>);

export const whoITeachSchema = z.object({
  ageGroups: z.object(dynamicAgeGroupSchemaObject),
  skillLevels: z.object(skillLevelSchemaObject),
});

export const lessonsLikeSchema = z.object({
  coachLessonDescription: z.string(),
});

const urlSchema = z
  .string()
  .optional()
  .or(z.literal('')) // Allow empty strings
  .transform((value) => (value ? (!value.startsWith('http') ? `https://${value}` : value) : value))
  .refine((value) => !value || /^https?:\/\/[\w.-]+(?:\.[\w.-]+)+[/#?]?.*$/.test(value), {
    message: 'Invalid URL',
  });

export const TagsAndLinksSchema = z.object({
  facebookUrl: urlSchema,
  instagramUrl: urlSchema,
  xUrl: urlSchema,
  linkedInUrl: urlSchema,
  youtubeUrl: urlSchema,
  websiteUrl: urlSchema,
  defaultSport: z.string().min(1, { message: 'Sport is required' }),
  sports: z.array(z.nativeEnum(SportsEnum)).min(1, { message: 'Please select atleast one sport' }),
});
